import React from "react";
import { TableColumnsType, Tooltip } from "antd";
import { ColumnFilterItem, CompareFn } from "antd/lib/table/interface";

import { ColumnConfig, ColumnType, InputType } from "./types";
import {
  renderArrayStringCell,
  renderDateCell,
  renderStatusCell,
  renderTextTooltipCell,
  renderThumbnailCell,
} from "./Cells";
import {
  createArrayStringSorter,
  createNumberSorter,
  createStatusSorter,
  createStringSorter,
} from "./sorters";
import { STATUS_FILTERS } from "./filters";

interface BuildColumnsState {
  order: string[];
  visibility: Record<string, boolean>;
  filters: Record<string, ColumnFilterItem[]>;
  thumbnailSize: number;
  editing: boolean;
}

const editInputType = (type: ColumnType): InputType | undefined => {
  switch (type) {
    case "text":
    case "arrayString":
      return InputType.Text;
    case "number":
      return InputType.Number;
    case "status":
      return InputType.Switch;
    default:
      return undefined;
  }
};

const buildSorter = (config: ColumnConfig) => {
  const field = config.dataIndex ?? config.key;
  switch (config.type) {
    case "number":
      return createNumberSorter(field);
    case "arrayString":
      return createArrayStringSorter(field);
    case "status":
      return createStatusSorter(field);
    default:
      // thumbnail, plain, text, date all sort as strings
      return createStringSorter(field);
  }
};

// Default column widths (px) per type. Drives horizontal scroll: when the sum
// of visible widths exceeds the container, antd shows a horizontal scrollbar.
// Override per column via ColumnConfig.width.
const defaultWidth = (config: ColumnConfig, thumbnailSize: number): number => {
  if (config.width !== undefined) return config.width;
  switch (config.type) {
    case "thumbnail":
      return thumbnailSize + 48;
    case "number":
      return 120;
    case "status":
      return 110;
    case "date":
      return 150;
    case "plain":
      return 140;
    default:
      // text, arrayString
      return 200;
  }
};

const buildRender = (config: ColumnConfig, thumbnailSize: number) => {
  if (config.render) {
    return config.render;
  }
  switch (config.type) {
    case "thumbnail":
      return renderThumbnailCell(config.key, thumbnailSize);
    case "status":
      return renderStatusCell(config.key);
    case "text":
      return renderTextTooltipCell;
    case "arrayString":
      return renderArrayStringCell;
    case "date":
      return renderDateCell;
    default:
      return undefined;
  }
};

// Header label. Wrapped in a tooltip by default (content = `title`), overridable
// via `config.tooltip`; `tooltip: false` renders a bare label with no tooltip.
const renderHeaderTitle = (config: ColumnConfig): React.ReactNode => {
  if (config.tooltip === false) {
    return <span>{config.title}</span>;
  }
  return (
    <Tooltip title={config.tooltip ?? config.title}>
      <span>{config.title}</span>
    </Tooltip>
  );
};

/**
 * Pure builder: derive antd columns from the config + current table state.
 * Replaces the chain of setColumns effects in the original pages.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export const buildColumns = <T extends object>(
  configs: ColumnConfig<T>[],
  state: BuildColumnsState,
): TableColumnsType<T> => {
  const { order, visibility, filters, thumbnailSize, editing } = state;
  const byKey = new Map(configs.map((c) => [c.key, c]));

  return (
    order
      .map((key) => byKey.get(key))
      .filter((c): c is ColumnConfig<T> => Boolean(c))
      // Hide = drop the column entirely. The zero-width trick does not collapse
      // under antd's auto table-layout (cell min-content keeps it visible), and
      // setting width on visible columns desyncs the header/body tables under
      // scroll.y. visibility is empty until loaded → treat unknown as visible.
      .filter((config) => visibility[config.key] !== false)
      .map((config) => {
        const dataIndex = config.dataIndex ?? config.key;
        // thumbnail/status read the whole record, so they expose no dataIndex
        const hasDataIndex =
          config.type !== "thumbnail" && config.type !== "status";
        const inputType = editInputType(config.type);

        const column: TableColumnsType<T>[number] = {
          key: config.key,
          title: renderHeaderTitle(config),
          align: "left",
          width: defaultWidth(config, thumbnailSize),
          render: buildRender(config, thumbnailSize),
          onHeaderCell: () => ({ id: config.key }),
          onCell: (record: T) => {
            const base: Record<string, unknown> = { id: config.key };
            if (config.editable && inputType !== undefined) {
              base.editing = editing;
              base.inputType = inputType;
              base.record = record;
              base.dataIndex = dataIndex;
            }
            return base;
          },
        };

        if (hasDataIndex) {
          column.dataIndex = dataIndex;
        }

        if (config.sortable === true) {
          column.sorter = buildSorter(config) as CompareFn<T>;
          // antd shows its own "click to sort" tooltip on sortable headers,
          // which collides with the header-title tooltip. Suppress it.
          column.showSorterTooltip = false;
        }

        if (config.type === "status") {
          column.filters = STATUS_FILTERS;
          column.onFilter = (value, record: T) => record[config.key] === value;
        } else if (config.filterable) {
          column.filters = filters[config.key] ?? [];
          column.onFilter = (value, record: T) => {
            const fieldValue = record[config.key];
            return Array.isArray(fieldValue)
              ? fieldValue.includes(value)
              : fieldValue === value;
          };
        }

        return column;
      })
  );
};
